import { CalendarRange, Clock } from 'lucide-react';
import type { Questionnaire } from '@peles/shared';
import { EventTypeSelect, SearchableListSelect } from '../common';
import { useTemplates } from '../../hooks/useTemplates';
import { useUpdateQuestionnaire } from '../../hooks/useQuestionnaires';

export interface QuestionnaireTemplateSettingsProps {
  questionnaire: Questionnaire;
}

/**
 * Template-level configuration: which event type this questionnaire template
 * belongs to and which timeline template its timeline-linked questions pick
 * their sections/entries from. Changes save immediately.
 */
export function QuestionnaireTemplateSettings({
  questionnaire,
}: QuestionnaireTemplateSettingsProps) {
  const { data: timelineTemplates = [], isLoading } = useTemplates('timeline');
  const updateQuestionnaire = useUpdateQuestionnaire();

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-4 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div data-testid="template-event-type">
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
            <CalendarRange size={14} />
            Event type
          </label>
          <EventTypeSelect
            value={questionnaire.eventTypeId}
            onChange={(eventTypeId) =>
              updateQuestionnaire.mutate({ id: questionnaire.id, eventTypeId })
            }
            disabled={updateQuestionnaire.isPending}
          />
          <p className="mt-1 text-xs text-gray-500">
            Tag-linked questions suggest this event type&apos;s tags.
          </p>
        </div>
        <div data-testid="template-timeline-template">
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
            <Clock size={14} />
            Linked timeline template
          </label>
          <SearchableListSelect
            items={timelineTemplates}
            value={questionnaire.timelineTemplateId}
            onChange={(next) =>
              updateQuestionnaire.mutate({
                id: questionnaire.id,
                timelineTemplateId: typeof next === 'string' && next ? next : null,
              })
            }
            getValue={(template) => template.id}
            getLabel={(template) => template.templateName || template.title || 'Untitled'}
            isLoading={isLoading}
            disabled={updateQuestionnaire.isPending}
            placeholder="No timeline template"
            ariaLabel="Linked timeline template"
            emptyMessage="No timeline templates yet."
          />
          <p className="mt-1 text-xs text-gray-500">
            Timeline-linked questions pick their section and entry from this template; using the
            questionnaire on an event auto-clones it when the event has no timeline from it yet.
          </p>
        </div>
      </div>
    </div>
  );
}
